import { useCallback } from 'react'

import {
  faColumns3,
  faDownload,
  faRedo,
  faSave,
  faSearch,
  faUndo,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Flex, Skeleton } from 'antd'
import { Tooltip } from 'govwell-ui'
import { Input } from 'govwell-ui'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import ManageColumnsDrawer from 'src/components/TableViews/ManageColumnsDrawer/ManageColumnsDrawer'
import { TABLE_VIEW_HEADER_BREAKPOINT_PX } from 'src/components/TableViews/util'
import Text from 'src/components/Typography/Text'
import { useCreateViewDashboardViewCsvMutation } from 'src/fetch/viewDashboards'
import { TableViewMode, useCreateViewCSVMutation } from 'src/fetch/views'
import { useCurrentBreakpoint } from 'src/hooks/use-current-breakpoint'
import useDisclosure from 'src/hooks/use-disclosure'
import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { TableViewManager } from 'src/models/TableViews/TableViewManager'
import { RefetchFnType } from 'src/types'
import { DateFormats, formatDate } from 'src/utils/date'

import AddFilterButton from './AddFilterButton'
import FilterTag from './FilterTag'

const HeaderContainer = styled(Flex)`
  container-type: inline-size;
`

const GhostButton = styled(Button)`
  border: solid 1px;
  border-radius: 4px;
  display: flex;
  align-items: baseline;
  overflow: hidden;
`

const ViewName = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const SearchRow = styled(Flex)`
  @container (width < ${TABLE_VIEW_HEADER_BREAKPOINT_PX}px) {
    justify-content: flex-start;
    gap: 6px;
  }
`

const ResultsCountContainer = styled.div`
  @container (width < ${TABLE_VIEW_HEADER_BREAKPOINT_PX}px) {
    display: none;
  }
`

const FilterRow = styled(Flex)`
  @container (width < ${TABLE_VIEW_HEADER_BREAKPOINT_PX}px) {
    flex-direction: column-reverse;
    gap: 12px;
  }
`

const FilterContainer = styled(Flex)`
  max-width: 100%;
  flex-direction: row;
  flex: 1;
`

type Props = {
  isLoadingViews: boolean
  isLoadingViewData: boolean
  mode: TableViewMode
  onSaveChanges: () => Promise<unknown>
  onSearchQueryChange: (value: string) => void
  refetch: RefetchFnType
  searchInputRef?: React.RefObject<HTMLInputElement>
  searchQuery: string
  tableViewManager: TableViewManager
  isCsvEnabled: boolean
}
const TableViewHeader = ({
  isLoadingViews,
  isLoadingViewData,
  mode,
  onSaveChanges,
  onSearchQueryChange,
  refetch,
  searchInputRef,
  searchQuery,
  tableViewManager,
  isCsvEnabled,
}: Props) => {
  const selectedView = tableViewManager.selectedView
  const manageColumnsDrawerState = useDisclosure()
  const { isOpen: isSaving, open: startSaving, close: stopSaving } = useDisclosure()
  const handleSave = useCallback(async () => {
    startSaving()
    await onSaveChanges()
    stopSaving()
  }, [onSaveChanges, startSaving, stopSaving])

  const { isOpen: isRefetching, open: startRefetching, close: stopRefetching } = useDisclosure()
  const handleRefetch = useCallback(async () => {
    startRefetching()
    await refetch()
    stopRefetching()
  }, [refetch, startRefetching, stopRefetching])

  const organizationId = useMyOrgId()
  const { mutateAsync: createViewCSV, isPending: isDownloadingViewCSV } = useCreateViewCSVMutation()
  const { mutateAsync: createViewDashboardViewCSV, isPending: isDownloadingViewDashboardViewCSV } =
    useCreateViewDashboardViewCsvMutation()
  const isDownloadingCSV = isDownloadingViewCSV || isDownloadingViewDashboardViewCSV
  const handleDownloadCSVClicked = useCallback(async () => {
    if (!selectedView) {
      return
    }
    const fileName = `Report - ${formatDate(new Date(), DateFormats.MonthNameDateTime)}`
    if (tableViewManager.viewDashboardId) {
      const viewInput = tableViewManager.getCreateViewInput(selectedView.protocol)
      const res = await createViewDashboardViewCSV({
        input: {
          viewDashboardId: tableViewManager.viewDashboardId,
          viewId: selectedView.id,
          fileName,
          searchQuery,
          sorts: viewInput.sorts,
          filters: viewInput.filters,
        },
      })
      window.location.href = res.createViewDashboardViewCsv.csvUrl
    } else {
      const res = await createViewCSV({
        fileName,
        input: {
          viewInput: tableViewManager.getCreateViewInput(selectedView.protocol),
          searchQuery,
        },
        organizationId,
      })
      window.location.href = res.createViewCsv.csvUrl
    }
  }, [
    selectedView,
    tableViewManager,
    createViewDashboardViewCSV,
    createViewCSV,
    searchQuery,
    organizationId,
  ])

  const { isSmallScreen } = useCurrentBreakpoint()

  if (!selectedView || isLoadingViews) {
    return (
      <Flex vertical gap="12px">
        <Flex gap="6px">
          <Skeleton.Input active />
          <Skeleton.Button active />
        </Flex>
        <Skeleton.Input active style={{ height: '22px' }} />
      </Flex>
    )
  }

  return (
    <HeaderContainer vertical justify="stretch" gap="12px">
      <SearchRow justify="space-between">
        <Flex align="center" gap="6px">
          <Input
            ariaLabel="Search input"
            isClearable
            placeholder="Search"
            prefixIcon={faSearch}
            ref={searchInputRef}
            value={searchQuery}
            onValueChange={onSearchQueryChange}
            width={isSmallScreen() ? '170px' : '240px'}
          />
          {!!selectedView.filterManager.filterableColumnTemplates.length && (
            <AddFilterButton filterManager={selectedView.filterManager} />
          )}
        </Flex>
        <Flex align="center" gap="6px">
          {selectedView.hasUnsavedChanges && (
            <ResultsCountContainer>
              {isLoadingViewData && (
                <Skeleton.Input
                  active
                  style={{
                    height: '22px',
                    width: '80px',
                    minWidth: '80px',
                  }}
                />
              )}
              {!isLoadingViewData && (
                <Flex style={{ marginRight: '3px' }}>
                  {selectedView.totalCount === 0 && (
                    <Text colorToken="colorTextSecondary" whiteSpace="nowrap">
                      No results
                    </Text>
                  )}
                  {selectedView.totalCount > 0 && (
                    <Text strong whiteSpace="nowrap">
                      {selectedView.totalCount === 1 && '1 Result'}
                      {selectedView.totalCount > 1 &&
                        `${Intl.NumberFormat('en-US').format(selectedView.totalCount)} Results`}
                    </Text>
                  )}
                </Flex>
              )}
            </ResultsCountContainer>
          )}
          <Tooltip content="Refresh results">
            <Button
              icon={<FontAwesomeIcon icon={faRedo} />}
              type="default"
              onClick={handleRefetch}
              loading={isRefetching}
            />
          </Tooltip>
          {isCsvEnabled && (
            <Tooltip content="Download CSV">
              <Button
                icon={<FontAwesomeIcon icon={faDownload} />}
                type="default"
                onClick={handleDownloadCSVClicked}
                loading={isDownloadingCSV}
              />
            </Tooltip>
          )}
          {mode === TableViewMode.Edit && (
            <Tooltip content="Manage columns">
              <Button
                icon={<FontAwesomeIcon icon={faColumns3} />}
                type="default"
                onClick={manageColumnsDrawerState.open}
              />
            </Tooltip>
          )}
          <ManageColumnsDrawer
            modalState={manageColumnsDrawerState}
            tableViewManager={tableViewManager}
            view={selectedView}
          />
        </Flex>
      </SearchRow>
      {(selectedView.hasUnsavedChanges ||
        selectedView.filterManager.filters.some((f) => f.isPopulated)) && (
        <FilterRow justify="space-between" gap="12px">
          <FilterContainer wrap="wrap" gap="8px">
            {selectedView.filterManager.filters.map((f) => (
              <FilterTag
                key={f.columnTemplateGeneratedId}
                filter={f}
                filterManager={selectedView.filterManager}
                tableViewMode={mode}
              />
            ))}
            {
              ' ' /* this space necessary to keep save changes right-aligned when no filters are applied */
            }
          </FilterContainer>
          <Flex gap="9px">
            {selectedView.hasUnsavedChanges && (
              <Button
                icon={<FontAwesomeIcon icon={faUndo} />}
                size="small"
                type="default"
                onClick={() => {
                  runInAction(() => {
                    selectedView.reset()
                  })
                }}
              >
                Reset
              </Button>
            )}
            {selectedView.hasUnsavedChanges && mode === TableViewMode.Edit && (
              <GhostButton
                icon={<FontAwesomeIcon icon={faSave} />}
                size="small"
                onClick={handleSave}
                loading={isSaving}
                type="link"
              >
                <span>Save changes to</span>&nbsp;
                <ViewName>{selectedView.name}</ViewName>
              </GhostButton>
            )}
          </Flex>
        </FilterRow>
      )}
    </HeaderContainer>
  )
}

export default observer(TableViewHeader)
